import {
  useGetExternalPaymentsSuspense,
  useGetInvoicesSuspense,
  useGetPartiesSuspense,
  usePostExternalPayments,
} from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewHeader,
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useQueryClient } from '@tanstack/react-query';

import type { CurrencyCode } from '@fragment/types';
import { Amount } from '@fragment/ui/src/components/Amount/Amount';
import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { TertiaryButton } from '@fragment/ui/src/components/Button/TertiaryButton/TertiaryButton';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalDropdown } from '@fragment/ui/src/components/Modal/ModalDropdown';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';

type PaymentRow = {
  transactionId: string;
  amount: string;
  currencyCode: CurrencyCode;
  accountReference: string;
  counterpartyId: string;
  created: string;
};

const emptyValue = {
  'Transaction ID': 'No Payments Yet',
  Amount: 'N/A',
  Account: 'N/A',
  Counterparty: 'N/A',
  Created: 'N/A',
};

const schema: ColumnDefinition<PaymentRow>[] = [
  {
    title: 'Transaction ID',
    key: 'transactionId',
    colspan: 'col-span-2',
  },
  {
    title: 'Amount',
    colspan: 'col-span-2',
    justify: 'right',
    format: (row: PaymentRow) => (
      <Amount
        amount={row.amount}
        currency={{
          code: row.currencyCode,
          precision: 2,
          name: row.currencyCode,
        }}
      />
    ),
  },
  {
    title: 'Account',
    key: 'accountReference',
    colspan: 'col-span-1',
  },
  {
    title: 'Counterparty',
    key: 'counterpartyId',
    colspan: 'col-span-1',
  },
  {
    title: 'Created',
    colspan: 'col-span-1',
    format: (row: PaymentRow) => (
      <span>{new Date(row.created).toLocaleDateString()}</span>
    ),
  },
];

type Props = {
  workspaceId: string;
};

const PaymentsListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Payments');

  const queryClient = useQueryClient();
  const { data } = useGetExternalPaymentsSuspense();
  const { data: invoicesData } = useGetInvoicesSuspense();
  const { data: partiesData } = useGetPartiesSuspense();
  const createPaymentMutation = usePostExternalPayments();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [amount, setAmount] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [accountReference, setAccountReference] = useState('');
  const [selectedInvoiceIk, setSelectedInvoiceIk] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');

  const getPath = useCallback(
    (payment: PaymentRow) =>
      makePath(
        ['w', workspaceId, 'payments', 'payments', payment.transactionId],
        { absolute: true }
      ),
    [workspaceId]
  );

  const payments: PaymentRow[] = useMemo(() => {
    return data.data.map((payment) => ({
      transactionId: payment.transactionId,
      amount: payment.amount,
      currencyCode: payment.currency_code,
      accountReference: payment.accountReference,
      counterpartyId: payment.counterpartyId,
      created: payment.created,
    }));
  }, [data]);

  const invoiceOptions = useMemo(
    () =>
      invoicesData.data.map((invoice) => ({
        id: invoice.id,
        ik: invoice.ik,
      })),
    [invoicesData]
  );

  const invoiceIks = useMemo(
    () => invoiceOptions.map((invoice) => invoice.ik),
    [invoiceOptions]
  );

  // Derive the invoice ID from the selected IK
  const invoiceId = useMemo(() => {
    if (!selectedInvoiceIk) return '';
    const invoice = invoiceOptions.find((inv) => inv.ik === selectedInvoiceIk);
    return invoice?.id ?? '';
  }, [selectedInvoiceIk, invoiceOptions]);

  const partyOptions = useMemo(
    () =>
      partiesData.data.map((party) => ({
        value: party.externalId,
        label: party.externalId,
      })),
    [partiesData]
  );

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTransactionId('');
    setAmount('');
    setCurrencyCode('USD');
    setAccountReference('');
    setSelectedInvoiceIk('');
    setCounterpartyId('');
  }, []);

  const handleCreatePayment = useCallback(async () => {
    try {
      await createPaymentMutation.mutateAsync({
        data: {
          transactionId,
          amount,
          currency_code: currencyCode as CurrencyCode,
          accountReference,
          invoiceId,
          counterpartyId,
        },
      });

      await queryClient.invalidateQueries({ queryKey: ['/external-payments'] });
      handleCloseModal();
    } catch (error) {
      console.error('Error creating payment:', error);
    }
  }, [
    createPaymentMutation,
    transactionId,
    amount,
    currencyCode,
    accountReference,
    invoiceId,
    counterpartyId,
    queryClient,
    handleCloseModal,
  ]);

  const isFormValid =
    transactionId && amount && accountReference && invoiceId && counterpartyId;

  return (
    <>
      <PaymentViewLayout
        header={<PaymentViewHeader>Payments</PaymentViewHeader>}
      >
        <div className="flex flex-col min-h-f1 w-full h-full">
          <div className="flex flex-row justify-end">
            <PrimaryButtonV3 onClick={handleOpenModal}>+</PrimaryButtonV3>
          </div>
          <NavTableV2
            textSize="md"
            data={payments.map((payment) => ({
              id: payment.transactionId,
              ...payment,
            }))}
            emptyValue={emptyValue}
            getPath={getPath}
            schema={schema}
            showHeader={false}
            stickyHeader={false}
          />
        </div>
      </PaymentViewLayout>

      {isModalOpen && (
        <Modal
          onClose={handleCloseModal}
          header="Create External Payment"
          onSubmit={(e) => {
            e.preventDefault();
            handleCreatePayment();
          }}
          body={
            <div className="flex flex-col space-y-f2">
              <ModalField
                label={<label htmlFor="transactionId">Transaction ID</label>}
              >
                <ModalTextInput
                  id="transactionId"
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="Unique transaction identifier"
                  data-testid="transaction-id-input"
                />
              </ModalField>
              <ModalField
                label={<label htmlFor="amount">Amount (cents)</label>}
              >
                <ModalTextInput
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount in cents"
                  data-testid="amount-input"
                />
              </ModalField>
              <ModalField
                label={<label htmlFor="currencyCode">Currency</label>}
              >
                <ModalDropdown
                  options={['USD', 'EUR', 'GBP', 'BTC', 'ETH', 'USDC']}
                  value={currencyCode}
                  onChange={setCurrencyCode}
                  placeholder="Select currency"
                  data-testid="currency-dropdown"
                />
              </ModalField>
              <ModalField
                label={<label htmlFor="accountReference">Account Ref</label>}
              >
                <ModalTextInput
                  id="accountReference"
                  type="text"
                  value={accountReference}
                  onChange={(e) => setAccountReference(e.target.value)}
                  placeholder="External account reference"
                  data-testid="account-reference-input"
                />
              </ModalField>
              <ModalField label={<label htmlFor="invoiceId">Invoice</label>}>
                <ModalDropdown
                  options={invoiceIks}
                  value={selectedInvoiceIk}
                  onChange={setSelectedInvoiceIk}
                  placeholder="Select an invoice"
                  data-testid="invoice-id-dropdown"
                />
              </ModalField>
              <ModalField
                label={<label htmlFor="counterpartyId">Counterparty</label>}
              >
                <ModalDropdown
                  options={partyOptions.map((party) => party.value)}
                  value={counterpartyId}
                  onChange={setCounterpartyId}
                  placeholder="Select a counterparty"
                  data-testid="counterparty-id-dropdown"
                />
              </ModalField>
            </div>
          }
          footer={
            <div className="flex justify-end gap-f2">
              <TertiaryButton onClick={handleCloseModal}>Cancel</TertiaryButton>
              <PrimaryButtonV3
                onClick={handleCreatePayment}
                disabled={!isFormValid || createPaymentMutation.isPending}
              >
                {createPaymentMutation.isPending
                  ? 'Creating...'
                  : 'Create Payment'}
              </PrimaryButtonV3>
            </div>
          }
        />
      )}
    </>
  );
};

export const PaymentsListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<PaymentViewLoading message="Loading payments..." />}>
    <PaymentsListPageContent workspaceId={workspaceId} />
  </Suspense>
);
